/* US shipping fee notice.

   Holds the customer at the last point before Shopify's checkout takes
   over, shows Adam's wording, and records that they saw it.

   The store is on Grow, so nothing can be injected inside the checkout
   itself. This intercepts the cart page checkout button, the cart form
   submit, the slide cart's Checkout button and plain links to /checkout.
   It cannot intercept the accelerated buttons (Shop Pay, PayPal, Google
   Pay) or a cart permalink, because those leave for Shopify's servers
   without the theme getting a look in.

   Acknowledgement is stored as a cart attribute rather than in
   sessionStorage on purpose: it survives a refresh and a device change,
   and it rides through to the order so Adam can see who was told. */

(function () {
  var root = document.querySelector('[data-nfd]');
  if (!root) return;

  var attributeName = root.getAttribute('data-nfd-attribute') || '_us_duties_ack';
  var acknowledged = root.getAttribute('data-nfd-acknowledged') === 'true';
  var panel = root.querySelector('.nfd__panel');
  var continueButton = root.querySelector('[data-nfd-continue]');

  /* Where to send them once they accept. Set when we intercept, because the
     cart page and the drawer submit different forms. */
  var pendingAction = null;
  var lastFocused = null;
  var busy = false;
  var continueLabel = continueButton ? continueButton.textContent : '';
  var savedOverflow = '';

  /* How long we wait for the cart API before going to checkout anyway. The
     write is bookkeeping; a slow network must not hold up a purchase. */
  var WRITE_TIMEOUT_MS = 3000;

  function focusable() {
    return Array.prototype.slice.call(
      panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    );
  }

  function trapFocus(event) {
    if (event.key !== 'Tab') return;
    var items = focusable();
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function onKeydown(event) {
    if (event.key === 'Escape') {
      close();
      return;
    }
    trapFocus(event);
  }

  function setBusy(state) {
    busy = state;
    if (!continueButton) return;
    continueButton.disabled = state;
    continueButton.textContent = state ? 'One moment' : continueLabel;
  }

  function open(action) {
    pendingAction = action;
    lastFocused = document.activeElement;
    root.hidden = false;
    /* Lock the page behind the dialog. The slide cart may have set its own
       overflow, so remember whatever was there and put it back on close. */
    savedOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeydown);
    if (continueButton) continueButton.focus();
  }

  function close() {
    pendingAction = null;
    root.hidden = true;
    document.documentElement.style.overflow = savedOverflow;
    document.removeEventListener('keydown', onKeydown);
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
  }

  /* Record the acknowledgement, then let them through. If the write fails
     or stalls we still let them through: a network blip on a bookkeeping
     call is not a reason to block someone from buying. The notice was shown
     and read, which is the part that matters to the customer.

     Back or Escape while the write is in flight wins: pendingAction is
     cleared by close(), so when the write settles we compare it to the
     action we captured and go nowhere if they changed their mind. The
     acknowledged flag is only set once we really are leaving, so a shopper
     who backed out is asked again next time. */
  function acknowledgeThenContinue() {
    if (busy) return;
    var proceed = pendingAction;
    if (typeof proceed !== 'function') {
      close();
      return;
    }
    setBusy(true);

    var attributes = {};
    attributes[attributeName] = new Date().toISOString();

    var write = fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).catch(function () {});

    var stall = new Promise(function (resolve) {
      setTimeout(resolve, WRITE_TIMEOUT_MS);
    });

    Promise.race([write, stall]).then(function () {
      setBusy(false);
      if (pendingAction !== proceed) return;
      acknowledged = true;
      root.setAttribute('data-nfd-acknowledged', 'true');
      close();
      proceed();
    });
  }

  if (continueButton) {
    continueButton.addEventListener('click', acknowledgeThenContinue);
  }

  Array.prototype.forEach.call(root.querySelectorAll('[data-nfd-cancel]'), function (el) {
    el.addEventListener('click', close);
  });

  /* What counts as a checkout button.

     The theme's own button is [name="checkout"] on the cart page and inside
     Dawn's cart drawer. That is not where most people leave from: the store
     runs the qikify slide cart, whose Checkout button is a plain <button>
     with no name, no form and no href. It navigates from its own click
     handler, so it walked straight past the first version of this guard and
     US shoppers who checked out from the drawer never saw the notice.

     Extra selectors come from the section setting so a future app can be
     added from the theme editor without touching this file. Links to
     /checkout are guarded too; /checkouts/... (an already-started checkout)
     is deliberately not, since by then it is too late. */
  var guardSelector = [
    '[name="checkout"]',
    '#checkout',
    '[data-nfd-guard]',
    'a[href="/checkout"]',
    'a[href^="/checkout?"]',
    'a[href$="/checkout"]'
  ]
    .concat(
      (root.getAttribute('data-nfd-guard-selectors') || '')
        .split(',')
        .map(function (s) { return s.trim(); })
        .filter(Boolean)
    )
    .join(', ');

  function findGuarded(node) {
    if (!node || !node.closest) return null;
    try {
      return node.closest(guardSelector);
    } catch (error) {
      /* A bad selector typed into the theme setting must not take the whole
         guard down with it. Fall back to the built-in list. */
      return node.closest('[name="checkout"], #checkout, [data-nfd-guard]');
    }
  }

  /* Capture phase on the document, so we get there before the theme's own
     cart handlers, before the slide cart's handler, and before the form
     submits. stopImmediatePropagation as well as stopPropagation, because an
     app that also delegates on the document would otherwise still run. */
  document.addEventListener(
    'click',
    function (event) {
      if (acknowledged) return;

      var target = findGuarded(event.target);
      if (!target) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      /* The cart page button lives OUTSIDE the cart form and is tied to it by
         form="cart", so closest('form') finds nothing. target.form follows
         that attribute. The slide cart button has neither, and that is fine:
         checkoutUrl falls back to the cart route the section rendered. */
      var form = target.form || target.closest('form');
      open(function () {
        if (target.href) {
          window.location.href = target.href;
        } else {
          window.location.href = checkoutUrl(form);
        }
      });
    },
    true
  );

  document.addEventListener(
    'submit',
    function (event) {
      if (acknowledged) return;

      /* Matched on id, not on action. The action is routes.cart_url, which
         carries a locale prefix on international storefronts, so comparing
         it to the literal "/cart" would quietly stop guarding the moment
         someone shops from another market. Which, for a US notice, is
         precisely the shopper it exists for. */
      var form = event.target;
      if (!form || form.id !== 'cart') return;

      event.preventDefault();
      event.stopPropagation();
      open(function () {
        window.location.href = checkoutUrl(form);
      });
    },
    true
  );

  /* Where "continue" sends them.

     This used to re-submit the cart form with the checkout button's own name
     added back by hand. That is the textbook way to do it and it is not
     reliable on this store: the post comes back as the cart page often enough
     that a customer can accept the notice, watch the box close, and find
     themselves still sitting on the cart with nothing to show for it. Going
     to the checkout URL is the same destination and behaves the same way
     every time. Nothing is lost by skipping the form post, because the theme
     already writes quantity changes to the cart over AJAX as they are made.

     The path is derived from the cart route rather than hard coded, because
     that route carries the market's locale prefix and a US shopper is exactly
     the person this notice exists for. The form's action is used when there
     is a form; the slide cart has none, so the section also renders
     routes.cart_url onto the root and that is the fallback. */
  function checkoutUrl(form) {
    var cartPath =
      (form && form.getAttribute('action')) ||
      root.getAttribute('data-nfd-cart-url') ||
      '/cart';
    var checkoutPath = cartPath.replace(/\/cart(\/)?(\?.*)?$/, '/checkout');
    return checkoutPath === cartPath ? '/checkout' : checkoutPath;
  }
})();
